import * as Dialog from "@radix-ui/react-dialog";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ChevronDown, ChevronRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, extractApiError } from "../lib/api";
import { createCountry } from "../lib/dataFetchers";
import type { Country, Entity, Region } from "../lib/types";
import { cn } from "../lib/cn";
import type { AxiosError } from "axios";
import { ALL_REGIONS } from "../lib/regionColors";
import { regionForIso2 } from "../lib/regionForIso2";

interface Props {
  open: boolean;
  onClose: () => void;
  countries: Country[];
}

/** Extract the backend error code/field from an AxiosError response. */
function getBackendCode(err: unknown): string | null {
  const detail = (err as AxiosError<{ code?: string; error?: string; detail?: unknown }>)
    ?.response?.data;
  if (!detail) return null;
  if (typeof detail === "object") {
    return (detail as Record<string, string>).code ?? (detail as Record<string, string>).error ?? null;
  }
  return null;
}

export function CreateEntityModal({ open, onClose, countries }: Props): JSX.Element {
  const { t } = useTranslation("entities");

  // ── Entity fields ────────────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [countryId, setCountryId] = useState(countries[0]?.id ?? "");
  const [legalForm, setLegalForm] = useState("");

  // ── New-country mode ─────────────────────────────────────────────────────────
  const [newCountryMode, setNewCountryMode] = useState(false);
  const [newCountryName, setNewCountryName] = useState("");
  const [newCountryIso2, setNewCountryIso2] = useState("");
  const [newCountryRegion, setNewCountryRegion] = useState<Region>(ALL_REGIONS[0]);
  const [newCountryFlag, setNewCountryFlag] = useState("");
  /**
   * True once the user has manually picked a region. The iso2 auto-derive then
   * stops overwriting their choice — the manual selection always wins, so a
   * country can be created with any region the user wants (not just the one
   * derived from its iso2).
   */
  const [regionTouched, setRegionTouched] = useState(false);
  /** Inline error messages for the country sub-form (null = no error). */
  const [countryError, setCountryError] = useState<string | null>(null);

  // Pre-fill region from iso2 as a convenience — but only while the user hasn't
  // chosen a region themselves (a manual pick must not be overwritten).
  useEffect(() => {
    if (regionTouched) return;
    if (!newCountryIso2 || newCountryIso2.length !== 2) return;
    const derived = regionForIso2(newCountryIso2);
    if (derived) setNewCountryRegion(derived);
  }, [newCountryIso2, regionTouched]);
  /**
   * After a successful country POST, the returned id is held here so that if
   * the entity POST subsequently fails the user can retry without re-POSTing
   * the country. When this is set, new-country mode is treated as "done".
   */
  const [createdCountryId, setCreatedCountryId] = useState<string | null>(null);

  const qc = useQueryClient();

  // ── Derived: which countryId will be sent with the entity POST ───────────────
  const resolvedCountryId = createdCountryId ?? (newCountryMode ? "" : countryId);

  // ── Reset helper ─────────────────────────────────────────────────────────────
  function handleClose() {
    // Reset the form (incl. the new-country sub-form) so stale values don't leak
    // into the next open cycle — the modal stays mounted, only `open` toggles.
    resetAll();
    onClose();
  }

  function resetAll() {
    setName("");
    setCountryId(countries[0]?.id ?? "");
    setLegalForm("");
    setNewCountryMode(false);
    setNewCountryName("");
    setNewCountryIso2("");
    setNewCountryRegion(ALL_REGIONS[0]);
    setRegionTouched(false);
    setNewCountryFlag("");
    setCountryError(null);
    setCreatedCountryId(null);
  }

  // ── Mutation ─────────────────────────────────────────────────────────────────
  const mutation = useMutation({
    mutationFn: async () => {
      let finalCountryId = resolvedCountryId;

      // Step 1 — create country if needed (and not already created in a prior attempt).
      if (newCountryMode && !createdCountryId) {
        const iso2Upper = newCountryIso2.trim().toUpperCase();
        const payload = {
          name: newCountryName.trim(),
          iso2: iso2Upper,
          region: newCountryRegion,
          ...(newCountryFlag.trim() ? { flag: newCountryFlag.trim() } : {}),
        };
        try {
          const created = await createCountry(payload);
          setCreatedCountryId(created.id);
          finalCountryId = created.id;
          // Invalidate countries so the dropdown + map + filters update immediately.
          void qc.invalidateQueries({ queryKey: ["countries"] });
        } catch (err) {
          const code = getBackendCode(err);
          if (code === "country_name_exists") {
            setCountryError(t("country_error_name_exists"));
          } else if (code === "iso2_exists") {
            setCountryError(t("country_error_iso2_exists"));
          } else {
            setCountryError(extractApiError(err));
          }
          // Abort — do NOT call the entity POST.
          throw err;
        }
      }

      // Step 2 — create entity.
      const body: Record<string, string> = {
        name,
        countryId: finalCountryId,
        status: "active",
      };
      if (legalForm) body.legalForm = legalForm;

      const res = await api.post<Entity>("/entities", body);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t("entity_created_toast"));
      resetAll();
      void qc.invalidateQueries({ queryKey: ["entities"] });
      void qc.invalidateQueries({ queryKey: ["entities-all"] });
      onClose();
    },
    onError: (err) => {
      // Country errors are surfaced inline (already set above); only show a toast
      // for entity-level errors.
      const code = getBackendCode(err);
      if (code !== "country_name_exists" && code !== "iso2_exists") {
        toast.error(extractApiError(err));
      }
    },
  });

  // Proto-aligned input/select styles (DLD §6 Entities / CreateEntityModal)
  const inputClass =
    "mt-1 w-full h-9 px-3 rounded-[8px] border border-border bg-surface-2 text-[13.5px] text-base focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none transition-[border-color,background,box-shadow]";
  const selectClass =
    "mt-1 w-full h-9 px-3 rounded-[8px] border border-border bg-surface-2 text-[13.5px] text-base focus:bg-surface focus:border-accent focus:ring-[3px] focus:ring-accent/[.14] focus:outline-none transition-[border-color,background,box-shadow]";
  const labelSpanClass = "text-[11px] font-bold tracking-[.04em] uppercase text-dim";

  // The submit button is disabled when:
  // - name is empty
  // - in existing-country mode and no country is selected
  // - in new-country mode (without a prior created id) and required fields are missing
  const newCountryReady =
    !!createdCountryId ||
    (newCountryName.trim().length > 0 &&
      newCountryIso2.trim().length === 2 &&
      /^[A-Za-z]{2}$/.test(newCountryIso2.trim()));
  const submitDisabled =
    mutation.isPending ||
    !name ||
    (newCountryMode ? !newCountryReady : !countryId);

  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/35" />
        <Dialog.Content
          className={cn(
            "fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50",
            "w-[min(520px,92vw)] bg-surface rounded-[11px] border border-border shadow-pop",
            "focus:outline-none p-6 max-h-[90dvh] overflow-y-auto",
          )}
        >
          <div className="flex items-start justify-between mb-5">
            <div>
              <Dialog.Title className="font-display font-semibold text-[17px] tracking-[-0.01em] text-text">
                {t("create_entity_title")}
              </Dialog.Title>
              <Dialog.Description className="text-[12px] text-muted mt-1">
                {t("create_entity_description")}
              </Dialog.Description>
            </div>
            <Dialog.Close
              aria-label={t("close_label")}
              className="h-9 w-9 inline-flex items-center justify-center rounded-[8px] border border-border bg-surface hover:bg-surface-2 text-muted transition-colors ml-3 shrink-0"
            >
              <X className="h-4 w-4" aria-hidden />
            </Dialog.Close>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              mutation.mutate();
            }}
            className="space-y-4"
          >
            <div className="space-y-3">
              {/* Entity name */}
              <label className="block">
                <span className={labelSpanClass}>{t("create_entity_name_label")} *</span>
                <input
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={inputClass}
                />
              </label>

              {/* Country select — shown when NOT in new-country mode (or after country created) */}
              {(!newCountryMode || createdCountryId) && (
                <label className="block">
                  <span className={labelSpanClass}>{t("create_entity_country_label")} *</span>
                  <select
                    required={!newCountryMode}
                    value={createdCountryId ?? countryId}
                    onChange={(e) => {
                      if (!createdCountryId) setCountryId(e.target.value);
                    }}
                    disabled={!!createdCountryId}
                    className={cn(selectClass, createdCountryId && "opacity-70 cursor-not-allowed")}
                  >
                    {countries.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.flag} {c.name}
                      </option>
                    ))}
                    {/* Newly created country appears only if it's in the refetched list */}
                  </select>
                </label>
              )}

              {/* + New country toggle button */}
              {!createdCountryId && (
                <button
                  type="button"
                  onClick={() => {
                    setNewCountryMode((v) => !v);
                    setCountryError(null);
                  }}
                  className={cn(
                    "flex items-center gap-1.5 text-xs font-medium min-h-[44px] px-0",
                    newCountryMode ? "text-accent" : "text-text-muted hover:text-accent",
                  )}
                  aria-expanded={newCountryMode}
                >
                  {newCountryMode ? (
                    <ChevronDown className="h-3.5 w-3.5" aria-hidden />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                  )}
                  {t("create_entity_add_country_toggle")}
                </button>
              )}

              {/* New-country sub-form */}
              {newCountryMode && !createdCountryId && (
                <div className="pl-4 border-l-2 border-accent/30 space-y-3">
                  {/* Country name */}
                  <label className="block">
                    <span className={labelSpanClass}>{t("create_entity_country_name_label")} *</span>
                    <input
                      required={newCountryMode}
                      value={newCountryName}
                      onChange={(e) => {
                        setNewCountryName(e.target.value);
                        setCountryError(null);
                      }}
                      className={cn(inputClass, countryError && "border-sev-expired focus:border-sev-expired")}
                      aria-describedby={countryError ? "country-error" : undefined}
                    />
                  </label>

                  {/* ISO2 */}
                  <label className="block">
                    <span className={labelSpanClass}>{t("create_entity_iso2_label")} *</span>
                    <input
                      required={newCountryMode}
                      value={newCountryIso2}
                      onChange={(e) => {
                        // Auto-uppercase, restrict to letters only, max 2 chars.
                        const val = e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase().slice(0, 2);
                        setNewCountryIso2(val);
                        setCountryError(null);
                      }}
                      maxLength={2}
                      pattern="[A-Za-z]{2}"
                      placeholder="e.g. BH"
                      className={cn(inputClass, countryError && "border-sev-expired focus:border-sev-expired")}
                      aria-describedby={countryError ? "country-error" : undefined}
                    />
                  </label>

                  {/* Region */}
                  <label className="block">
                    <span className={labelSpanClass}>{t("create_entity_region_label")} *</span>
                    <select
                      required={newCountryMode}
                      value={newCountryRegion}
                      onChange={(e) => {
                        setNewCountryRegion(e.target.value as Region);
                        setRegionTouched(true);
                      }}
                      className={selectClass}
                    >
                      {ALL_REGIONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>

                  {/* Flag (optional) */}
                  <label className="block">
                    <span className={labelSpanClass}>{t("create_entity_flag_label")}</span>
                    <input
                      value={newCountryFlag}
                      onChange={(e) => setNewCountryFlag(e.target.value)}
                      placeholder="🌐"
                      className={inputClass}
                    />
                  </label>

                  {/* Inline country error */}
                  {countryError && (
                    <p id="country-error" role="alert" className="text-xs text-sev-expired">
                      {countryError}
                    </p>
                  )}
                </div>
              )}

              {/* Confirmation pill after successful country creation */}
              {createdCountryId && newCountryMode && (
                <p className="text-[12px] text-green-text font-semibold">
                  {t("create_entity_country_created_ok")}
                </p>
              )}

              {/* Legal form */}
              <label className="block">
                <span className={labelSpanClass}>{t("create_entity_legal_form_label")}</span>
                <input
                  value={legalForm}
                  onChange={(e) => setLegalForm(e.target.value)}
                  placeholder="e.g. SARL, GmbH, LLP…"
                  className={inputClass}
                />
              </label>
            </div>

            <p className="text-[11.5px] text-dim">
              {t("create_entity_hint")}
            </p>

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={submitDisabled}
                className="inline-flex min-h-[44px] sm:min-h-[34px] h-[34px] items-center justify-center rounded-lg bg-accent px-3 text-[12.5px] font-semibold text-white hover:bg-[#4338ca] active:bg-[#3730a3] disabled:opacity-50 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20"
              >
                {t("create_entity_submit")}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex min-h-[44px] sm:min-h-[34px] h-[34px] items-center justify-center rounded-lg border border-border bg-surface px-3 text-[12.5px] font-semibold text-text hover:bg-surface-2 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-accent/20"
              >
                {t("create_entity_cancel")}
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
